/**
 * JoinHandler — processes a collaboration invite when the SPA is opened at
 * .../?join=<token> (standalone, from a shared invite link).
 *
 * Flow: preview the invite → the recipient taps Join → registration is required
 * (guests are routed to the auth sheet; once registered we claim the invite) →
 * joinTrip switches the active trip to the shared one and lands on the Trip tab.
 * Renders nothing when there is no ?join= token.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useItinerary } from '../context/ItineraryContext';
import { useView } from '../context/ViewContext';
import { previewInvite, type EmbedInvitePreview } from '../api/itineraryApi';

type Phase =
  | 'loading'
  | 'preview'
  | 'joining'
  | 'done'
  | 'joined_no_switch'
  | 'invalid';

function clearJoinParam() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('join');
    window.history.replaceState(null, '', url.toString());
  } catch {
    /* no-op */
  }
}

export default function JoinHandler() {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const { joinTrip } = useItinerary();
  const { setView } = useView();

  const [token] = useState<string | null>(() => {
    try {
      return new URLSearchParams(window.location.search).get('join');
    } catch {
      return null;
    }
  });
  const [preview, setPreview] = useState<EmbedInvitePreview | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  // Set when the recipient opts in but must register first — claim once the
  // auth sheet reports 'authenticated'.
  const wantsJoinRef = useRef(false);

  // Load the preview once.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    previewInvite(token).then(p => {
      if (cancelled) return;
      if (!p || !p.valid) setPhase('invalid');
      else {
        setPreview(p);
        setPhase('preview');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const doJoin = useCallback(async () => {
    if (!token) return;
    setPhase('joining');
    const res = await joinTrip(token);
    wantsJoinRef.current = false;
    if (!res) {
      setPhase('invalid');
      return;
    }
    clearJoinParam();
    if (res.switched) {
      setPhase('done');
      setView('itinerary');
    } else {
      // Single-trip instance: membership recorded but the active trip is
      // unchanged. Confirm rather than silently doing nothing.
      setPhase('joined_no_switch');
    }
  }, [token, joinTrip, setView]);

  // Once the recipient has registered (auth sheet → authenticated), claim it.
  useEffect(() => {
    if (wantsJoinRef.current && authState === 'authenticated') {
      void doJoin();
    }
  }, [authState, doJoin]);

  const onJoin = () => {
    if (authState === 'authenticated') {
      void doJoin();
    } else {
      // Registration required — open the auth sheet; the effect above claims
      // the invite once it completes.
      wantsJoinRef.current = true;
      setView('auth');
    }
  };

  if (!token || phase === 'done') return null;

  return (
    <div className="wg-join" role="dialog" aria-label={t('collab.join_title')}>
      <div className="wg-join__card">
        {phase === 'invalid' || phase === 'joined_no_switch' ? (
          <>
            <h2 className="wg-join__title">
              {phase === 'joined_no_switch'
                ? t('collab.join_recorded')
                : t('collab.join_invalid')}
            </h2>
            <button
              type="button"
              className="btn"
              onClick={() => {
                clearJoinParam();
                setView('chat');
              }}
            >
              {t('collab.join_dismiss')}
            </button>
          </>
        ) : phase === 'joining' ? (
          <p className="wg-join__title">{t('collab.join_joining')}</p>
        ) : (
          <>
            <p className="wg-join__eyebrow">
              {preview?.invited_by_name
                ? t('collab.join_by', { name: preview.invited_by_name })
                : t('collab.join_generic')}
            </p>
            <h2 className="wg-join__title">{preview?.title}</h2>
            <p className="wg-join__role">
              {t('collab.join_as', {
                role: t(`collab.role_${preview?.role ?? 'viewer'}`),
              })}
            </p>
            <button type="button" className="btn primary" onClick={onJoin}>
              {t('collab.join_cta')}
            </button>
            {authState !== 'authenticated' && (
              <p className="wg-join__note">{t('collab.join_register_note')}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
